import { Routes, CanActivateFn } from '@angular/router';
import { inject } from '@angular/core';
import { MsalGuard } from '@azure/msal-angular';
import { AppConfigService } from './core/config/app-config.service';
import { userRouteAccessGuard } from './core/auth/user-route-access.guard';
import { orderManagementRouteGuard } from './order-management/order-management-route.guard';
import { systemModuleAccessGuard } from './core/auth/system-module-access.guard';
import { CandidateSessionService } from './core/auth/candidate-session.service';
import { blockCandidateFromKnowledgeBaseGuard } from './core/auth/knowledge-base-access.guard';
import { ErrorComponent } from './layouts/error/error.component';
import { AccessDeniedComponent } from './layouts/access-denied/access-denied.component';
import { LandingPageComponent } from './layouts/landing-page/landing-page.component';

/**
 * Returns the appropriate top-level guard based on AppConfigService.microsoftLogin.
 *
 * - Microsoft/Azure AD: delegates to MsalGuard (triggers redirect to Microsoft login
 *   if no active session, waits for redirect promise, then allows navigation).
 * - LDAP: delegates to userRouteAccessGuard (checks identity via backend API).
 */
const microsoftOrUserGuard: CanActivateFn = (route, state) => {
  const appConfig = inject(AppConfigService);
  const candidateSession = inject(CandidateSessionService);
  // KCS-565 Part 2: a candidate (temp user) session on MSL must NOT be sent through MsalGuard (which would
  // redirect to Microsoft). It is authenticated by its own JWT, so it takes the userRouteAccessGuard path.
  if (appConfig.microsoftLogin && !candidateSession.isActive()) {
    // Not a candidate session → always go through MsalGuard, which redirects to Microsoft when the
    // user isn't signed in. Client decision 2026-07-16: permanent employees must never see a /login
    // hub — an unauthenticated MSL user is always sent straight to Microsoft. Candidates reach their
    // login only via the public /qr-login (QR scan) and /candidate-login routes, which are guard-free.
    return inject(MsalGuard).canActivate(route, state);
  }
  return userRouteAccessGuard(route, state);
};

/**
 * Guard for the public /login route. On MSL, permanent employees must never see the login page —
 * redirect to Microsoft BEFORE the component renders (a redirect from LoginComponent.ngOnInit would
 * flash the /login page first — the post-logout jitter). On-prem, or when a candidate session is
 * active, /login renders normally (LDAP form / candidate context). Client decision 2026-07-16.
 */
const loginRouteGuard: CanActivateFn = (route, state) => {
  const appConfig = inject(AppConfigService);
  const candidateSession = inject(CandidateSessionService);
  if (appConfig.microsoftLogin && !candidateSession.isActive()) {
    return inject(MsalGuard).canActivate(route, state);
  }
  return true;
};

export const routes: Routes = [
  // ─── Public routes (no auth guard) ───
  {
    path: 'checklist-lock-approve',
    loadComponent: () => import('./shared/checklist-lock-approve/checklist-lock-approve.component').then(m => m.ChecklistLockApproveComponent),
  },

  // ─── Fullscreen routes (no AppMainComponent wrapper) ───
  // Cable Harness simulator family — auth-protected, feature-gated.
  // microsoftOrUserGuard handles fresh-direct-link auth (MSAL redirect or LDAP /login);
  // userRouteAccessGuard enforces `cable.harness.manager.show` feature flag.
  {
    path: 'cableh/:id',
    data: {
      features: ['cable.harness.manager.show'],
    },
    loadComponent: () => import('./cable-harness/cable-harness/cable-harness.component').then(m => m.CableHarnessComponent),
    canActivate: [microsoftOrUserGuard, userRouteAccessGuard],
  },
  {
    path: 'cableh/board/:boardKey',
    data: {
      features: ['cable.harness.manager.show'],
    },
    loadComponent: () => import('./cable-harness/harness-manager/configurer-components/board-configurer/board-configurer.component').then(m => m.BoardConfigurerComponent),
    canActivate: [microsoftOrUserGuard, userRouteAccessGuard],
  },
  {
    path: 'cableh/pmodule/:boardKey/:pmoduleKey',
    data: {
      features: ['cable.harness.manager.show'],
    },
    loadComponent: () => import('./cable-harness/harness-manager/configurer-components/pmodule-configurer/pmodule-configurer.component').then(m => m.PmoduleConfigurerComponent),
    canActivate: [microsoftOrUserGuard, userRouteAccessGuard],
  },
  {
    path: 'cableh/tester/:boardKey/:pmoduleKey',
    data: {
      features: ['cable.harness.manager.show'],
    },
    loadComponent: () => import('./cable-harness/cable-harness-tester/cable-harness-tester.component').then(m => m.CableHarnessTesterComponent),
    canActivate: [microsoftOrUserGuard, userRouteAccessGuard],
  },
  // The :nodeKey is for deep-linkability/debugging; the actual node + practice
  // context is handed off via CableHarnessRunnerSessionService.
  {
    path: 'cableh/runner/:nodeKey',
    data: {
      features: ['checklist.show', 'my.elearning.show', 'cable.harness.manager.show'],
    },
    loadComponent: () => import('./cable-harness/cable-harness/cable-harness.component').then(m => m.CableHarnessComponent),
    canActivate: [microsoftOrUserGuard, userRouteAccessGuard],
  },
  {
    path: 'checklist-fullscreen/:key',
    data: {
      features: ['checklist.show'],
      trainingCheclist: false,
    },
    loadComponent: () => import('./checklist/checklist.component').then(m => m.ChecklistComponent),
    canActivate: [userRouteAccessGuard],
  },
  {
    path: 'free-order-fullscreen/:key',
    data: {
      features: ['checklist.show'],
    },
    loadComponent: () => import('./checklist/free-order-checklist/free-order-checklist.component').then(m => m.FreeOrderChecklistComponent),
    canActivate: [userRouteAccessGuard],
  },
  {
    path: 'video-editor/:videoFileKey',
    data: {},
    loadComponent: () => import('./video-editor/video-editor.component').then(m => m.VideoEditorComponent),
    canActivate: [microsoftOrUserGuard, userRouteAccessGuard],
  },
  {
    path: 'ai-checklist-editor/:sessionKey',
    data: {
      features: ['node.create.checklist'],
    },
    loadComponent: () => import('./ai-checklist-editor/ai-checklist-editor.component').then(m => m.AiChecklistEditorComponent),
    canActivate: [microsoftOrUserGuard, userRouteAccessGuard],
  },
  {
    path: 'node-fullscreen/:node',
    data: {},
    loadComponent: () => import('./node/node.component').then(m => m.NodeComponent),
    canActivate: [userRouteAccessGuard, blockCandidateFromKnowledgeBaseGuard],
  },

  // ─── Login route ───
  // loginRouteGuard: on MSL this redirects to Microsoft before LoginComponent renders (no /login
  // flash). On-prem / active candidate → renders normally. Candidate routes below stay guard-free.
  {
    path: 'login',
    canActivate: [loginRouteGuard],
    loadComponent: () => import('./shared/login/login.component').then(m => m.LoginComponent),
  },

  // ─── KCS-565 Part 2: candidate QR entry point (public, no MSAL guard) ───
  // Printed QR cards / phone-camera scans open here. The LoginComponent reads `qrLogin` to force candidate
  // mode and auto-resolves the `?token=` param into the "Log in as <name>?" confirm. Works on MSL too.
  {
    path: 'qr-login',
    data: { qrLogin: true },
    loadComponent: () => import('./shared/login/login.component').then(m => m.LoginComponent),
  },

  // ─── KCS-565: dedicated candidate login entry (public, no MSAL guard) ───
  // Bookmarkable direct link to the candidate screen (username+password + QR scan). The LoginComponent reads
  // `candidateLogin` to open in candidate mode; if candidates aren't enabled it redirects to /login.
  {
    path: 'candidate-login',
    data: { candidateLogin: true },
    loadComponent: () => import('./shared/login/login.component').then(m => m.LoginComponent),
  },

  // ─── Main layout wrapper ───
  {
    path: '',
    loadComponent: () =>
      import('./layouts/main/app-main.component').then(m => m.AppMainComponent),
    canActivate: [microsoftOrUserGuard],
    children: [
      // Home (default route)
      {
        path: '',
        data: {},
        canActivate: [userRouteAccessGuard],
        loadChildren: () => import('./home/home.routes').then(m => m.HOME_ROUTES),
      },

      // Landing page for users without root nodes
      {
        path: 'nonode',
        data: {
          authorities: [],
        },
        component: LandingPageComponent,
        canActivate: [userRouteAccessGuard],
      },

      // Access denied
      {
        path: 'accessdenied',
        data: {
          authorities: [],
        },
        component: AccessDeniedComponent,
      },

      // Node management
      {
        path: 'node',
        data: {},
        loadChildren: () => import('./node/node.routes').then(m => m.NODE_ROUTES),
        canActivate: [userRouteAccessGuard, blockCandidateFromKnowledgeBaseGuard],
      },

      // Knowledge Graph (merged feature module — replaces the old /knowledgeGraph static SPA)
      {
        path: 'knowledge-graph',
        data: {},
        loadChildren: () => import('./knowledge-graph/knowledge-graph.routes').then(m => m.KNOWLEDGE_GRAPH_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Dictionary
      {
        path: 'dictionary',
        data: {
          features: ['dictionary.show'],
        },
        loadChildren: () => import('./app-dictionary/dictionary.routes').then(m => m.DICTIONARY_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Qualification Matrix
      {
        path: 'qualificationmatrix',
        data: {
          features: ['qualificationmatrix.show'],
        },
        loadChildren: () => import('./app-qualification-matrix/qualification-matrix.routes').then(m => m.QM_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Order Management
      {
        path: 'ordermanagement',
        data: {
          // orderManagementRouteGuard enforces this feature ONLY for direct Organizer access; a SAP
          // checklist deep-link (?Order=...) bypasses it (pass-through to /checklist, which guards itself).
          features: ['order.management.show'],
          authorities: ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_AM'],
        },
        loadChildren: () => import('./order-management/order-management.routes').then(m => m.ORDER_MANAGEMENT_ROUTES),
        canActivate: [orderManagementRouteGuard],
      },

      // Planner
      {
        path: 'planner',
        data: {
          features: ['planner.show'],
        },
        loadChildren: () => import('./planner/planner.routes').then(m => m.PLANNER_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Evaluation Checklist
      {
        path: 'evaluation-checklist',
        data: {
          features: ['checklist.evaluation.show'],
        },
        loadChildren: () => import('./evaluation-checklist/evaluation-checklist.routes').then(m => m.EVALUATION_CHECKLIST_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Release
      {
        path: 'release',
        data: {
          features: ['release.show'],
        },
        loadChildren: () => import('./release/release.routes').then(m => m.RELEASE_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Linked Checklist
      {
        path: 'linkedChecklist',
        data: {
          features: ['linkedChecklist.show'],
        },
        loadChildren: () => import('./linked-checklist/linked-checklist.routes').then(m => m.LINKED_CHECKLIST_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Media Library
      {
        path: 'medialib',
        data: {
          features: ['medialibrary.show'],
        },
        loadChildren: () => import('./shared/media-library/media-library.routes').then(m => m.MEDIA_LIBRARY_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Archive (same MediaLibraryComponent as /medialib, with archive flag)
      {
        path: 'archive',
        data: {
          archive: true,
          features: ['archive.show'],
        },
        loadChildren: () => import('./shared/media-library/media-library.routes').then(m => m.MEDIA_LIBRARY_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Checklist
      {
        path: 'checklist',
        data: {
          features: ['checklist.show'],
          trainingCheclist: false,
        },
        loadChildren: () => import('./checklist/checklist.routes').then(m => m.CHECKLIST_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Admin
      {
        path: 'admin',
        data: {
          authorities: ['ROLE_ADMIN'],
        },
        loadChildren: () => import('./admin/admin.routes').then(m => m.ADMIN_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Statistics
      {
        path: 'statistics',
        data: {
          authorities: ['ROLE_USER'],
        },
        loadChildren: () => import('./statistics/statistics.routes').then(m => m.STATISTICS_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Jump To
      {
        path: 'jumpto',
        loadChildren: () => import('./jumpto/jumpto.routes').then(m => m.JUMPTO_ROUTES),
        canActivate: [blockCandidateFromKnowledgeBaseGuard],
      },

      // Audit
      {
        path: 'audit',
        loadChildren: () => import('./app-gemba/gemba.routes').then(m => m.GEMBA_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // TPM (Total Productive Maintenance)
      {
        path: 'tpm',
        loadChildren: () => import('./app-tpm/tpm.routes').then(m => m.TPM_ROUTES),
        canActivate: [userRouteAccessGuard, systemModuleAccessGuard],
      },

      // E-Learning
      {
        path: 'elearning',
        data: {
          features: ['elearning.show'],
        },
        loadChildren: () => import('./app-elearning/elearning.routes').then(m => m.ELEARNING_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // My E-Learning
      {
        path: 'my-elearning',
        data: {
          features: ['my.elearning.show'],
        },
        loadChildren: () => import('./app-elearning/elearning.routes').then(m => m.MY_ELEARNING_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // E-Learning Information Center
      {
        path: 'elearning/information-center',
        data: {
          features: ['elearning.information_center.show'],
        },
        loadChildren: () => import('./app-elearning/elearning.routes').then(m => m.ELEARNING_INFO_CENTER_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // E-Learning Evaluation
      {
        path: 'elearning/elearning-evaluation',
        data: {
          features: ['evaluation.center.show'],
        },
        loadChildren: () => import('./app-elearning/elearning.routes').then(m => m.ELEARNING_EVALUATION_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      {
        path: 'elearning/one-on-one-training',
        data: {
          features: ['elearning.one_on_one_training.show'],
        },
        loadChildren: () => import('./app-elearning/elearning.routes').then(m => m.ELEARNING_ONE_ON_ONE_TRAINING_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Survey
      {
        path: 'survey',
        data: {
          features: ['survey.show'],
        },
        loadChildren: () => import('./app-survey/survey.routes').then(m => m.SURVEY_ROUTES),
        canActivate: [userRouteAccessGuard, systemModuleAccessGuard],
      },

      // Survey Analytics
      {
        path: 'survey-analytics',
        data: {
          features: ['survey.analytics.show'],
        },
        loadChildren: () => import('./app-survey-analytics/survey-analytics.routes').then(m => m.SURVEY_ANALYTICS_ROUTES),
        canActivate: [userRouteAccessGuard, systemModuleAccessGuard],
      },

      // My Survey
      {
        path: 'my-survey',
        data: {
          features: ['my.survey.show'],
        },
        loadChildren: () => import('./app-survey/survey.routes').then(m => m.MY_SURVEY_ROUTES),
        canActivate: [userRouteAccessGuard, systemModuleAccessGuard],
      },

      // My Reviews — survey submissions awaiting reviewer approval
      {
        path: 'my-reviews',
        data: {
          features: ['my.reviews.show'],
        },
        loadChildren: () => import('./app-survey/survey.routes').then(m => m.MY_REVIEWS_ROUTES),
        canActivate: [userRouteAccessGuard, systemModuleAccessGuard],
      },

      // Harness Manager
      {
        path: 'harnessmanager',
        data: {
          features: ['cable.harness.manager.show'],
        },
        loadChildren: () => import('./cable-harness/cable-harness.routes').then(m => m.CABLE_HARNESS_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Qualification
      {
        path: 'qualification',
        data: {
          authorities: [],
        },
        loadChildren: () => import('./app-qualification-matrix/qualification-matrix.routes').then(m => m.QUALIFICATION_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // My Skillset
      {
        path: 'my-skillset',
        data: {
          features: ['my.skillset.show'],
        },
        loadChildren: () => import('./app-qualification-matrix/qualification-matrix.routes').then(m => m.MY_SKILLSET_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Meine Beauftragungen (Vorgesetzter erteilt offene Beauftragungen)
      {
        path: 'my-commissioning',
        data: {
          features: ['my.commissioning.show'],
        },
        loadChildren: () => import('./app-qualification-matrix/qualification-matrix.routes').then(m => m.MY_COMMISSIONING_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // My Training — hidden globally, route disabled. Uncomment to re-enable.
      // {
      //   path: 'mytraining',
      //   data: {
      //     features: ['my.training.show'],
      //   },
      //   loadChildren: () => import('./app-qualification-matrix/qualification-matrix.routes').then(m => m.MY_TRAINING_ROUTES),
      //   canActivate: [userRouteAccessGuard],
      // },

      // My Confirmations — gated by `settings.createTrainingChecklist` YML flag
      {
        path: 'my-confirmations',
        loadChildren: () => import('./my-confirmations/my-confirmations.routes').then(m => m.MY_CONFIRMATIONS_ROUTES),
        canActivate: [userRouteAccessGuard, systemModuleAccessGuard],
      },

      // My Work
      {
        path: 'my-work',
        data: {
          features: ['my.work.show'],
        },
        loadComponent: () => import('./order-management/app-my-work/app-my-work.component').then(m => m.AppMyWorkComponent),
        canActivate: [userRouteAccessGuard],
      },

      // Information Center
      {
        path: 'information-center',
        data: {
          authorities: [],
        },
        loadChildren: () => import('./app-qualification-matrix/qualification-matrix.routes').then(m => m.QM_INFO_CENTER_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Evaluation Center (mapped from elearning evaluation path)
      {
        path: 'evaluation-center',
        data: {
          features: ['evaluation.center.show'],
        },
        loadChildren: () => import('./app-qualification-matrix/qualification-matrix.routes').then(m => m.QM_EVAL_CENTER_ROUTES),
        canActivate: [userRouteAccessGuard],
      },

      // Help / Contact
      {
        path: 'help/contact',
        data: {
          authorities: ['ROLE_USER'],
        },
        loadChildren: () => import('./contact/contact.routes').then(m => m.CONTACT_ROUTES),
        canActivate: [userRouteAccessGuard],
      },
    ],
  },

  // ─── Error routes ───
  {
    path: 'error',
    component: ErrorComponent,
    data: {
      authorities: [],
      pageTitle: 'error.title',
    },
  },
  {
    path: '404',
    component: ErrorComponent,
    data: {
      authorities: [],
      pageTitle: 'error.title',
      error404: true,
    },
  },

  // ─── Catch-all redirect to 404 ───
  {
    path: '**',
    redirectTo: '/404',
  },
];
